/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, November 12th 2021, 1:24:44 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { schemaBuilder, Many2OneIntPKBaseSchema, Many2ManyIntPKBaseSchema } from "lib/validation";
import {
  ProcessTemplateArtifact,
  ProcessTemplateArtifactValidationSchema,
  ProcessTemplateArtifactSchemaType,
} from "lib/schemas";
import { WritableModel } from "lib/schemas/abstract";

export enum TARGET_USERS {
  applicant = "Submitter",
  staff = "Worker",
}

export enum TARGET_USERS_DESC {
  applicant = "The use that made the submission will receive this notification",
  staff = "All workers with permission to do, assign or supervise this task will receive this notification",
}

export enum NOTIFICATION_TYPES {
  email = "Email",
  push = "Push Notification (Via Mobile Application)",
  sms = "SMS",
}

export enum SEND_WHEN_OPTIONS {
  task_assignment = "TO BE ASSIGNED",
  task_review = "TO BE PERFORMED",
  task_supervise = "TO BE SUPERVISED",
  task_done = "DONE",
  task_undone = "UNDONE",
}

export enum SEND_WHEN_OPTIONS_DESC {
  task_assignment = "Task needs to be assigned.",
  task_review = "Task needs to be worked on. If the task is not assigned to a specific user, all users with permission to perform the task will receive this notification.",
  task_supervise = "Task needs to be supervised. This notification will be sent when a supervisor is selected during assignment.",
  task_done = "Task is complete. A DONE task notification will be sent to the user who performed the task or the submission owner depending on who you set in the 'Send To' field. You can add additional users to include in the notification with the 'Send to Teams' field.",
  task_undone = "Task is undone. When a task is undone, a task notification will be sent to the user who performed the task or the submission owner depending on who you set in the 'Send To' field. You can add additional users to include in the notification with the 'Send to Teams' field.",
}

export enum RECIPIENTS_SEND_METHODS {
  group_message = "One Message to All Recipients",
  individual_message = "Individual Message to Each Recipient",
}

export enum RECIPIENTS_COPY_METHODS {
  to = "To",
  cc = "Carbon Copy (CC)",
  bcc = "Blind Carbon Copy (BCC)",
}

export interface NotificationTemplate extends ProcessTemplateArtifact, WritableModel<false> {
  title: string;
  message: string;
  type: keyof typeof NOTIFICATION_TYPES;
  target_users: keyof typeof TARGET_USERS;
  send_when: keyof typeof SEND_WHEN_OPTIONS;
  expiry_date?: any;
  enabled: boolean;
  attachments?: string[];
  task_template?: number;
  additional_recipients_groups?: number[];
  multiple_recipients_send_method?: string;
  multiple_recipients_copy_method?: string;
}

type WriteOnlyNotificationTemplate = Omit<NotificationTemplate, "id">;

export const NotificationTemplateValidationSchema: ProcessTemplateArtifactSchemaType<WriteOnlyNotificationTemplate> =
  ProcessTemplateArtifactValidationSchema.shape({
    title: schemaBuilder.string().required("Notification message title is required"),
    message: schemaBuilder.string().required("Notification message body is required"),
    type: schemaBuilder.mixed().oneOf(Object.keys(NOTIFICATION_TYPES)).required("Notification type is required"),
    target_users: schemaBuilder.mixed().oneOf(Object.keys(TARGET_USERS)).required("Target user is required"),
    send_when: schemaBuilder.mixed().oneOf(Object.keys(SEND_WHEN_OPTIONS)).required("Send when is required"),
    expiry_date: schemaBuilder.date().optional().nullable(),
    enabled: schemaBuilder.boolean().required("Enabled flag is required"),
    attachments: schemaBuilder.array(schemaBuilder.string()).optional().nullable(),
    task_template: Many2OneIntPKBaseSchema().required("Task Template is required"), // Required here though its optional on the backend. For now, a notification config must be linked to a task template. Other use cases will drive change in this behaviour.
    additional_recipients_groups: Many2ManyIntPKBaseSchema().optional().nullable(),
    multiple_recipients_send_method: schemaBuilder.string().optional().nullable(),
    multiple_recipients_copy_method: schemaBuilder.string().optional().nullable(),
  });
